import React, { createContext, useContext, useReducer, useEffect } from 'react'
import apiService from '../services/api'
import printerService from '../services/printerService'
import { useAuth } from './AuthContext'

// Initial state
const initialState = {
  currentOrganization: null,
  organizationId: null,
  organizations: [],
  branches: [],
  materials: [],
  transfers: [],
  activeLabelTemplate: null,
  isLoading: false,
  error: null
}

// Action types
const ORG_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_CURRENT_ORGANIZATION: 'SET_CURRENT_ORGANIZATION',
  SET_ORGANIZATIONS: 'SET_ORGANIZATIONS',
  ADD_ORGANIZATION: 'ADD_ORGANIZATION',
  UPDATE_ORGANIZATION: 'UPDATE_ORGANIZATION',
  DELETE_ORGANIZATION: 'DELETE_ORGANIZATION',
  SET_BRANCHES: 'SET_BRANCHES',
  ADD_BRANCH: 'ADD_BRANCH',
  UPDATE_BRANCH: 'UPDATE_BRANCH',
  DELETE_BRANCH: 'DELETE_BRANCH',
  SET_MATERIALS: 'SET_MATERIALS',
  ADD_MATERIAL: 'ADD_MATERIAL',
  UPDATE_MATERIAL: 'UPDATE_MATERIAL',
  DELETE_MATERIAL: 'DELETE_MATERIAL',
  SET_TRANSFERS: 'SET_TRANSFERS',
  ADD_TRANSFER: 'ADD_TRANSFER',
  UPDATE_TRANSFER: 'UPDATE_TRANSFER',
  SET_ACTIVE_LABEL_TEMPLATE: 'SET_ACTIVE_LABEL_TEMPLATE'
}

// Reducer
function organizationReducer(state, action) {
  switch (action.type) {
    case ORG_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload }

    case ORG_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false }

    case ORG_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null }

    case ORG_ACTIONS.SET_CURRENT_ORGANIZATION:
      const newState = { ...state, currentOrganization: action.payload, organizationId: action.payload?.id ?? null };
      return newState;

    case ORG_ACTIONS.SET_ORGANIZATIONS:
      return { ...state, organizations: action.payload, isLoading: false }

    case ORG_ACTIONS.ADD_ORGANIZATION:
      return {
        ...state,
        organizations: [...state.organizations, action.payload],
        isLoading: false
      }

    case ORG_ACTIONS.UPDATE_ORGANIZATION:
      return {
        ...state,
        organizations: state.organizations.map(org =>
          org.id === action.payload.id ? action.payload : org
        ),
        currentOrganization: state.currentOrganization?.id === action.payload.id
          ? action.payload
          : state.currentOrganization,
        isLoading: false
      }

    case ORG_ACTIONS.DELETE_ORGANIZATION:
      return {
        ...state,
        organizations: state.organizations.filter(org => org.id !== action.payload),
        currentOrganization: state.currentOrganization?.id === action.payload
          ? null
          : state.currentOrganization,
        isLoading: false
      }

    case ORG_ACTIONS.SET_BRANCHES:
      return { ...state, branches: action.payload, isLoading: false }

    case ORG_ACTIONS.ADD_BRANCH:
      return {
        ...state,
        branches: [...state.branches, action.payload],
        isLoading: false
      }

    case ORG_ACTIONS.UPDATE_BRANCH:
      return {
        ...state,
        branches: state.branches.map(branch =>
          branch.id === action.payload.id ? action.payload : branch
        ),
        isLoading: false
      }

    case ORG_ACTIONS.DELETE_BRANCH:
      return {
        ...state,
        branches: state.branches.filter(branch => branch.id !== action.payload),
        isLoading: false
      }

    case ORG_ACTIONS.SET_MATERIALS:
      return { ...state, materials: action.payload, isLoading: false }

    case ORG_ACTIONS.ADD_MATERIAL:
      return {
        ...state,
        materials: [...state.materials, action.payload],
        isLoading: false
      }

    case ORG_ACTIONS.UPDATE_MATERIAL:
      return {
        ...state,
        materials: state.materials.map(material =>
          material.id === action.payload.id ? action.payload : material
        ),
        isLoading: false
      }

    case ORG_ACTIONS.DELETE_MATERIAL:
      return {
        ...state,
        materials: state.materials.filter(material => material.id !== action.payload),
        isLoading: false
      }

    case ORG_ACTIONS.SET_TRANSFERS:
      return { ...state, transfers: action.payload, isLoading: false }

    case ORG_ACTIONS.ADD_TRANSFER:
      return {
        ...state,
        transfers: [...state.transfers, action.payload],
        isLoading: false
      }

    case ORG_ACTIONS.UPDATE_TRANSFER:
      return {
        ...state,
        transfers: state.transfers.map(transfer =>
          transfer.id === action.payload.id ? action.payload : transfer
        ),
        isLoading: false
      }

    case ORG_ACTIONS.SET_ACTIVE_LABEL_TEMPLATE:
      return { ...state, activeLabelTemplate: action.payload }

    default:
      return state
  }
}

// Create context
const OrganizationContext = createContext()

// Organization provider component
export function OrganizationProvider({ children }) {
  const [state, dispatch] = useReducer(organizationReducer, initialState)
  const { isAuthenticated, user } = useAuth()

  // Initialize current organization from localStorage
  useEffect(() => {
    const savedOrganization = localStorage.getItem('currentOrganization')
    if (savedOrganization) {
      try {
        const organization = JSON.parse(savedOrganization)
        setCurrentOrganization(organization)
      } catch (error) {
        console.warn('❌ [OrganizationContext] Failed to parse saved organization:', error)
        localStorage.removeItem('currentOrganization')
      }
    } else {
      console.log('⚠️ [OrganizationContext] Nenhuma organização salva no localStorage');
    }
  }, [])

  // Set current organization from user data when authenticated
  useEffect(() => {

    if (isAuthenticated && user && user.organization) {
      // Set the user's organization as current organization
      setCurrentOrganization(user.organization)
    } else {

    }
  }, [isAuthenticated, user])

  // Load user's organizations when authenticated
  useEffect(() => {
    if (isAuthenticated && user && user.organization) {
      const org = user.organization ? { ...user.organization, dominio: user.organization?.dominio ?? user.organization?.domain ?? user.organization?.dominio } : user.organization
      dispatch({ type: ORG_ACTIONS.SET_ORGANIZATIONS, payload: [org] })

      // Set as current organization if none is selected
      if (!state.currentOrganization) {
        setCurrentOrganization(user.organization)
      }
    }
  }, [isAuthenticated, user])

  // Load current organization data when it changes
  useEffect(() => {
    if (state.currentOrganization && isAuthenticated) {
      loadOrganizationData(state.currentOrganization.id).catch(error => {
        console.warn('Failed to load organization data:', error.message)
      })

    }
  }, [state.currentOrganization, isAuthenticated])

  // Helper function to handle errors
  const handleError = (error) => {
    console.error('Organization context error:', error)
    dispatch({ type: ORG_ACTIONS.SET_ERROR, payload: error.message })
  }

  // Load organizations - DEPRECATED: Now using organization from user data
  // const loadOrganizations = async () => {
  //   dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
  //   try {
  //     const organizations = await apiService.organizations.getAll()
  //     dispatch({ type: ORG_ACTIONS.SET_ORGANIZATIONS, payload: organizations })

  //     // Set first organization as current if none selected
  //     if (organizations.length > 0 && !state.currentOrganization) {
  //       setCurrentOrganization(organizations[0])
  //     }
  //   } catch (error) {
  //     handleError(error)
  //     // Return empty array to prevent further errors
  //     dispatch({ type: ORG_ACTIONS.SET_ORGANIZATIONS, payload: [] })
  //     throw error // Re-throw to allow caller to handle
  //   }
  // }

  // Load organization data
  const loadOrganizationData = async (orgId) => {
    try {
      // Data loading temporarily disabled

      // Initialize empty arrays for now
      dispatch({ type: ORG_ACTIONS.SET_BRANCHES, payload: [] })
      dispatch({ type: ORG_ACTIONS.SET_MATERIALS, payload: [] })
      dispatch({ type: ORG_ACTIONS.SET_TRANSFERS, payload: [] })
    } catch (error) {
      handleError(error)
      throw error // Re-throw to allow caller to handle
    }
  }

  const refreshActiveLabelTemplate = async () => {
    dispatch({ type: ORG_ACTIONS.SET_ACTIVE_LABEL_TEMPLATE, payload: null })
    try { printerService.setActiveLabelCode(null) } catch { }
    return null
  }

  // Set current organization
  const setCurrentOrganization = (organization) => {
    const org = organization ? { ...organization, dominio: organization?.dominio ?? organization?.domain ?? organization?.dominio } : organization
    dispatch({ type: ORG_ACTIONS.SET_CURRENT_ORGANIZATION, payload: org })
    localStorage.setItem('currentOrganization', JSON.stringify(org))
  }

  // Organization CRUD operations
  const createOrganization = async (orgData) => {
    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      // const newOrg = await apiService.organizations.create(orgData)
      const newOrg = { id: Date.now(), ...orgData } // Temporary mock
      dispatch({ type: ORG_ACTIONS.ADD_ORGANIZATION, payload: newOrg })
      return { success: true, organization: newOrg }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  const updateOrganization = async (orgId, orgData) => {
    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      // const updatedOrg = await apiService.organizations.update(orgId, orgData)
      const updatedOrg = { id: orgId, ...orgData } // Temporary mock
      dispatch({ type: ORG_ACTIONS.UPDATE_ORGANIZATION, payload: updatedOrg })
      return { success: true, organization: updatedOrg }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  const deleteOrganization = async (orgId) => {
    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      // await apiService.organizations.delete(orgId)
      console.log('Delete organization:', orgId) // Temporary mock
      dispatch({ type: ORG_ACTIONS.DELETE_ORGANIZATION, payload: orgId })
      return { success: true }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  // Branch CRUD operations
  const createBranch = async (branchData) => {
    if (!state.currentOrganization) return { success: false, error: 'No organization selected' }

    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      const newBranch = await apiService.branches.create(state.currentOrganization.id, branchData)
      dispatch({ type: ORG_ACTIONS.ADD_BRANCH, payload: newBranch })
      return { success: true, branch: newBranch }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  const updateBranch = async (branchId, branchData) => {
    if (!state.currentOrganization) return { success: false, error: 'No organization selected' }

    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      const updatedBranch = await apiService.branches.update(state.currentOrganization.id, branchId, branchData)
      dispatch({ type: ORG_ACTIONS.UPDATE_BRANCH, payload: updatedBranch })
      return { success: true, branch: updatedBranch }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  const deleteBranch = async (branchId) => {
    if (!state.currentOrganization) return { success: false, error: 'No organization selected' }

    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      await apiService.branches.delete(state.currentOrganization.id, branchId)
      dispatch({ type: ORG_ACTIONS.DELETE_BRANCH, payload: branchId })
      return { success: true }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  // Material CRUD operations
  const createMaterial = async (materialData) => {
    if (!state.currentOrganization) return { success: false, error: 'No organization selected' }

    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      const newMaterial = await apiService.materials.create(state.currentOrganization.id, materialData)
      dispatch({ type: ORG_ACTIONS.ADD_MATERIAL, payload: newMaterial })
      return { success: true, material: newMaterial }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  const updateMaterial = async (materialId, materialData) => {
    if (!state.currentOrganization) return { success: false, error: 'No organization selected' }

    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      const updatedMaterial = await apiService.materials.update(state.currentOrganization.id, materialId, materialData)
      dispatch({ type: ORG_ACTIONS.UPDATE_MATERIAL, payload: updatedMaterial })
      return { success: true, material: updatedMaterial }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  const deleteMaterial = async (materialId) => {
    if (!state.currentOrganization) return { success: false, error: 'No organization selected' }

    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      await apiService.materials.delete(state.currentOrganization.id, materialId)
      dispatch({ type: ORG_ACTIONS.DELETE_MATERIAL, payload: materialId })
      return { success: true }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  // Transfer operations
  const createTransfer = async (transferData) => {
    if (!state.currentOrganization) return { success: false, error: 'No organization selected' }

    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      const newTransfer = await apiService.transfers.create(state.currentOrganization.id, transferData)
      dispatch({ type: ORG_ACTIONS.ADD_TRANSFER, payload: newTransfer })
      return { success: true, transfer: newTransfer }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  const updateTransferStatus = async (transferId, action) => {
    if (!state.currentOrganization) return { success: false, error: 'No organization selected' }

    dispatch({ type: ORG_ACTIONS.SET_LOADING, payload: true })
    try {
      let updatedTransfer
      switch (action) {
        case 'approve':
          updatedTransfer = await apiService.transfers.approve(state.currentOrganization.id, transferId)
          break
        case 'reject':
          updatedTransfer = await apiService.transfers.reject(state.currentOrganization.id, transferId)
          break
        case 'cancel':
          updatedTransfer = await apiService.transfers.cancel(state.currentOrganization.id, transferId)
          break
        default:
          throw new Error('Invalid action')
      }

      dispatch({ type: ORG_ACTIONS.UPDATE_TRANSFER, payload: updatedTransfer })
      return { success: true, transfer: updatedTransfer }
    } catch (error) {
      handleError(error)
      return { success: false, error: error.message }
    }
  }

  // Clear error
  const clearError = () => {
    dispatch({ type: ORG_ACTIONS.CLEAR_ERROR })
  }

  // Context value
  const value = {
    ...state,
    organizationId: state.currentOrganization?.id ?? state.organizationId ?? null,

    setCurrentOrganization,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    createBranch,
    updateBranch,
    deleteBranch,
    createMaterial,
    updateMaterial,
    deleteMaterial,
    createTransfer,
    updateTransferStatus,
    refreshActiveLabelTemplate,
    clearError
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

// Custom hook to use organization context
export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}

export default OrganizationContext